import {
  ALL_SALARY_WORK_TIMES_QUERY,
  type SalaryWorkTimeNode,
} from '@/modules/companies'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ComboboxCommonProps, ComboboxItem } from './combobox'
import { Combobox } from './combobox'

interface CommonProps extends ComboboxCommonProps<string> {
  alwaysInclude?: Omit<SalaryWorkTimeNode, '__typename'> | null
  exclude?: string[]
}

interface SingleProps extends CommonProps {
  mode: 'single'
  value: string
  clearable?: boolean
  onChange(value: string): void
}

interface MultiProps extends CommonProps {
  mode: 'multiple'
  value: string[]
  clearable?: false
  onChange(value: string[]): void
}

type Props = SingleProps | MultiProps

export function SalarySettingCombobox({
  mode,
  value,

  alwaysInclude = null,
  exclude = [],
  clearable = false,

  placeholder,
  searchPlaceholder,
  noResultsText,

  onChange,

  ...props
}: Props) {
  const translations = useTranslate({
    single: {
      placeholder: 'components.combobox.leader-group.placeholder',
      searchPlaceholder: 'components.combobox.leader-group.search-placeholder',
      noResults: 'components.combobox.leader-group.no-results',
    },
    multiple: {
      placeholder: 'components.multi-combobox.leader-group.placeholder',
      searchPlaceholder:
        'components.multi-combobox.leader-group.search-placeholder',
      noResults: 'components.multi-combobox.leader-group.no-results',
    },
  })

  const { data: salaryWorkTimesData } = useQuery(ALL_SALARY_WORK_TIMES_QUERY)

  const items: ComboboxItem[] = (() => {
    const salaryWorkTimes = salaryWorkTimesData?.allSalaryWorkTimes ?? []

    const items = salaryWorkTimes
      .filter(
        salaryWorkTime =>
          salaryWorkTime.id !== alwaysInclude?.id &&
          !exclude.includes(salaryWorkTime.id)
      )
      .map<ComboboxItem>(salaryWorkTime => ({
        value: salaryWorkTime.id,
        label: salaryWorkTime.name,
      }))

    if (!!alwaysInclude)
      items.unshift({
        value: alwaysInclude.id,
        label: alwaysInclude.name,
      })

    return items
  })()

  if (mode === 'single')
    return (
      <Combobox
        {...props}
        mode="single"
        value={value}
        items={items}
        clearable={clearable}
        placeholder={placeholder ?? translations.single.placeholder}
        searchPlaceholder={
          searchPlaceholder ?? translations.single.searchPlaceholder
        }
        noResultsText={noResultsText ?? translations.single.noResults}
        onChange={onChange}
      />
    )

  return (
    <Combobox
      {...props}
      mode="multiple"
      value={value}
      items={items}
      placeholder={placeholder ?? translations.multiple.placeholder}
      searchPlaceholder={
        searchPlaceholder ?? translations.multiple.searchPlaceholder
      }
      noResultsText={noResultsText ?? translations.multiple.noResults}
      onChange={onChange}
    />
  )
}
